import React from 'react';
import { Modal, Typography, Table, Button, Tag, Space } from 'antd';
import moment from 'moment-timezone';

const { Title, Text } = Typography;

const ClassDetailsModal = ({ visible, onClose, classData, teacherData }) => {
  
  // Get teacher name
  const getTeacherName = () => {
    if (!teacherData) return '-';
    return `${teacherData.nickname || ''} ${teacherData.firstname || ''} ${teacherData.lastname || ''}`.trim();
  };

  // Get status tag
  const getStatusTag = (status) => {
    if (!status) return <Tag color="default">No Status</Tag>;
    
    switch(status.toLowerCase()) {
      case 'approved':
        return <Tag color="green">Approved</Tag>;
      case 'pending':
        return <Tag color="orange">Pending</Tag>;
      case 'cancelled':
        return <Tag color="red">Cancelled</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };
  
  // Format date only
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    try {
      const date = moment(dateString);
      if (date.isValid()) {
        return date.format('DD MMM YYYY');
      }
      return '-';
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  // Format time only
  const formatTime = (timeString) => {
    if (!timeString) return '-';
    
    try {
      const time = moment(timeString, 'HH:mm:ss');
      if (time.isValid()) {
        return time.format('HH:mm');
      }
      return timeString;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  // Format duration to "2:30hrs" format
  const formatDuration = (durationString) => {
    if (!durationString) return '-';
    
    try {
      // Handle format like "2h30m" or "2 30"
      if (durationString.includes('h') && durationString.includes('m')) {
        const hours = parseInt(durationString.split('h')[0]);
        const minutes = parseInt(durationString.split('h')[1].replace('m', ''));
        return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
      }
      
      // Handle format like "2 30" (hours and minutes with space)
      if (durationString.includes(' ')) {
        const [hours, minutes] = durationString.split(' ').map(part => parseInt(part));
        return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
      }
      
      // Try to convert minutes to hours:minutes format
      const totalMinutes = parseInt(durationString);
      if (!isNaN(totalMinutes)) {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
      }
      
      // If we can't parse it, return as is
      return durationString;
    } catch (error) {
      console.error('Error formatting duration:', error);
      return durationString || '-';
    }
  };

  // Only render the data if classData exists
  const dataSource = classData ? [
    {
      key: 'class_code',
      label: 'CLASS CODE',
      value: classData.class_code || '-'
    },
    {
      key: 'teacher',
      label: 'TEACHER',
      value: getTeacherName() 
    },
    {
      key: 'date',
      label: 'DATE',
      value: formatDate(classData.date)
    },
    {
      key: 'time',
      label: 'TIME',
      value: `${formatTime(classData.schedule_time)} - ${formatTime(classData.end_time)}`
    },
    {
      key: 'duration',
      label: 'HOUR',
      value: formatDuration(classData.schedule_hour || classData.duration)
    },
    {
      key: 'classroom',
      label: 'CLASSROOM',
      value: classData.classroom_number || '-'
    }
  ] : [];

  const columns = [
    {
      dataIndex: 'label',
      key: 'label',
      width: '30%',
      render: label => <span style={{ fontWeight: 'bold' }}>{label}</span>,
    },
    {
      dataIndex: 'value',
      key: 'value',
      width: '70%',
    },
  ];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width={700}
      footer={[

      ]}
      centered
    >
      {classData ? (
        <div>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <Title level={2} style={{ marginBottom: '8px' }}>{classData.subject_name}</Title>
            <Space>
              {getStatusTag(classData.status)}
              <Text type="secondary">ID: {classData.classid}</Text>
            </Space>
          </div>

          <Table
            dataSource={dataSource}
            columns={columns}
            pagination={false}
            showHeader={false}
            rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
            bordered
          />
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Text type="secondary">No class details available</Text>
        </div>
      )}
    </Modal>
  );
};

export default ClassDetailsModal;